/**
 * Workbench Filter Service
 *
 * Provides advanced filtering for workbench playlists with audio features
 * and genre support.
 */

import { getApiClient } from '../client';

export interface FilterCriteria {
  bpm?: { min: number; max: number };
  energy?: { min: number; max: number };
  valence?: { min: number; max: number };
  danceability?: { min: number; max: number };
  genres?: string[];
  minGenreWeight?: number;
  artists?: string[];
  artistMode?: 'include' | 'exclude';
}

export interface WorkbenchFilterPreviewResponse {
  matchedCount: number;
  totalCount: number;
  matchedTrackIds: string[];
}

export interface WorkbenchFilterResponse {
  action: string;
  matchedCount: number;
  totalCount: number;
  playlistId?: string;
  playlistUri?: string;
  removedCount?: number;
  remainingCount?: number;
}

export type FilterAction = 'create_playlist' | 'remove_filtered' | 'keep_only_filtered';

export const workbenchService = {
  /**
   * Preview filter results without applying
   *
   * @param playlistId - Spotify playlist ID
   * @param trackIds - Array of track IDs to filter (avoids backend playlist fetch)
   * @param criteria - Filter criteria (audio features and genres)
   * @returns Preview with match count and track IDs
   */
  async previewFilter(
    playlistId: string,
    trackIds: string[],
    criteria: FilterCriteria
  ): Promise<WorkbenchFilterPreviewResponse> {
    return getApiClient().post<WorkbenchFilterPreviewResponse>(
      `/workbench/playlists/${encodeURIComponent(playlistId)}/filter/preview`,
      {
        track_ids: trackIds,
        ...criteria,
      }
    );
  },

  /**
   * Apply filter and perform action (create/remove/keep)
   *
   * @param playlistId - Spotify playlist ID
   * @param request - Filter criteria and action to perform
   * @returns Result with operation details
   */
  async applyFilter(
    playlistId: string,
    request: FilterCriteria & {
      action: FilterAction;
      newPlaylistName?: string;
    }
  ): Promise<WorkbenchFilterResponse> {
    return getApiClient().post<WorkbenchFilterResponse>(
      `/workbench/playlists/${encodeURIComponent(playlistId)}/filter/apply`,
      request
    );
  },
};
